import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Badge, Button, Modal, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendarAlt, faCheckCircle, faTimesCircle,
  faUserCheck, faClock, faBook, faChartLine,
  faFilter, faStar, faClipboardCheck, faUserGraduate,
  faExclamationTriangle, faListAlt, faFileExcel, faFilePdf,
} from '@fortawesome/free-solid-svg-icons';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import Layout from '../../../../app/Layout';
import HeaderContent from '../../General/HeaderContent';
import Breadcrumb from '../../General/Breadcrumb';
import SpinnerLoading from '../../../components/Spinners/SpinnerLoading';
import authAxios from '../../../../config/authAxios';
import { avisoError } from '../../../components/AvisosMensajes/MensajesSwalf';
import { obtenerKpisAcademicos, obtenerProductosKpi, obtenerDocentesKpi } from '../../../../actions/kpisAcademicosActions';

/* ─── Helpers ──────────────────────────────────────────────────── */
const colorSemaforo = (pct, sinDatos = false) => {
  if (sinDatos || pct === null) return '#6c757d';
  if (pct >= 90) return '#28a745';
  if (pct >= 70) return '#fd7e14';
  return '#dc3545';
};

const labelSemaforo = (pct, sinDatos = false) => {
  if (sinDatos || pct === null) return 'Sin datos';
  if (pct >= 90) return 'Cumple';
  if (pct >= 70) return 'En riesgo';
  return 'No cumple';
};

const colorEstado = { verde: '#28a745', amarillo: '#fd7e14', rojo: '#dc3545' };

/* ─── Tarjeta KPI ──────────────────────────────────────────────── */
const KpiCard = ({ titulo, valor, unidad = '%', meta = 90, icon, sinDatos = false }) => {
  const num   = parseFloat(valor) || 0;
  const color = colorSemaforo(sinDatos ? null : num, sinDatos);
  const barW  = sinDatos ? 0 : Math.min(100, num);
  return (
    <div className="col-xl-3 col-md-4 col-sm-6 mb-3">
      <div className="box p-3 h-100" style={{ borderTop: `4px solid ${color}` }}>
        <div className="d-flex align-items-start justify-content-between mb-2">
          <div style={{ width: 38, height: 38, borderRadius: 8, backgroundColor: `${color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <FontAwesomeIcon icon={icon} style={{ color, fontSize: 17 }} />
          </div>
          {!sinDatos && <FontAwesomeIcon icon={num >= meta ? faCheckCircle : faTimesCircle} style={{ color: num >= meta ? '#28a745' : '#dc3545', fontSize: 17 }} />}
        </div>
        <h2 style={{ color, fontWeight: 700, fontSize: 30, marginBottom: 2 }}>{sinDatos ? '—' : `${num}${unidad}`}</h2>
        <p className="mb-2" style={{ fontSize: 12, fontWeight: 600, lineHeight: 1.3, color: '#444' }}>{titulo}</p>
        <div style={{ height: 4, backgroundColor: '#eee', borderRadius: 2 }}>
          <div style={{ height: 4, width: `${barW}%`, backgroundColor: color, borderRadius: 2, transition: 'width .4s' }} />
        </div>
        {!sinDatos && <small className="text-muted" style={{ fontSize: 10 }}>Meta: {meta}%</small>}
      </div>
    </div>
  );
};

/* ─── Fila KPI en bloque ───────────────────────────────────────── */
const KpiFila = ({ label, cumplidos, total, obligatorio = false }) => {
  const pct   = total > 0 ? Math.round((cumplidos / total) * 100) : null;
  const color = colorSemaforo(pct, pct === null);
  return (
    <div className="d-flex align-items-center justify-content-between py-2" style={{ borderBottom: '1px solid #f0f0f0', fontSize: 14 }}>
      <div className="d-flex align-items-center" style={{ flex: 1 }}>
        <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: color, marginRight: 10, flexShrink: 0 }} />
        <span style={{ fontWeight: 500 }}>{label}</span>
        {obligatorio && <Badge variant="warning" className="ml-2" style={{ fontSize: 10 }}>Obligatorio</Badge>}
      </div>
      <div className="text-center" style={{ minWidth: 80 }}>
        <span style={{ fontWeight: 600 }}>{pct !== null ? `${pct}%` : '—'}</span>
        <small className="d-block text-muted" style={{ fontSize: 10 }}>{total > 0 ? `${cumplidos}/${total}` : 'Sin datos'}</small>
      </div>
      <div style={{ minWidth: 90, textAlign: 'right' }}>
        <span style={{ fontSize: 11, fontWeight: 600, color, backgroundColor: `${color}15`, padding: '2px 8px', borderRadius: 20 }}>
          {labelSemaforo(pct, pct === null)}
        </span>
      </div>
    </div>
  );
};

/* ─── Bloque contenedor ────────────────────────────────────────── */
const BloqueKpi = ({ titulo, icon, pct, sinDatos, onVerMas, children }) => {
  const color = colorSemaforo(pct, sinDatos);
  return (
    <div className="box mb-3">
      <div className="box-header d-flex align-items-center justify-content-between" style={{ borderBottom: `3px solid ${color}`, padding: '14px 18px' }}>
        <div className="d-flex align-items-center">
          <FontAwesomeIcon icon={icon} style={{ color, fontSize: 18, marginRight: 10 }} />
          <span style={{ fontWeight: 700, fontSize: 15 }}>{titulo}</span>
        </div>
        <div className="d-flex align-items-center">
          {!sinDatos && <span style={{ fontSize: 22, fontWeight: 700, color, marginRight: 12 }}>{pct}%</span>}
          <span style={{ fontSize: 11, fontWeight: 600, color, backgroundColor: `${color}15`, padding: '3px 10px', borderRadius: 20, marginRight: onVerMas ? 10 : 0 }}>
            {labelSemaforo(pct, sinDatos)}
          </span>
          {onVerMas && (
            <Button variant="outline-secondary" size="sm" style={{ fontSize: 11, padding: '2px 10px' }} onClick={onVerMas}>
              <FontAwesomeIcon icon={faListAlt} className="mr-1" />Ver más
            </Button>
          )}
        </div>
      </div>
      <div className="box-body" style={{ padding: '8px 18px 14px' }}>{children}</div>
    </div>
  );
};

/* ─── Badge estado recurso ─────────────────────────────────────── */
const BadgeRecurso = ({ estado }) => {
  if (!estado || estado === 'Sin registro') return <span className="text-muted" style={{ fontSize: 11 }}>—</span>;
  const map = { 'Completo': '#28a745', 'Pendiente': '#dc3545', 'En proceso': '#fd7e14' };
  const color = map[estado] || '#6c757d';
  return (
    <span style={{ fontSize: 10, fontWeight: 600, color, backgroundColor: `${color}20`, padding: '1px 6px', borderRadius: 10 }}>
      {estado}
    </span>
  );
};

/* ═══════════════════════════════════════════════════════════════ */
const ReporteKpis = ({ loading, datos, productos, docentes, obtenerKpisAcademicos, obtenerProductosKpi, obtenerDocentesKpi }) => {
  const now = new Date();
  const [anio,        setAnio]       = useState(now.getFullYear().toString());
  const [mes,         setMes]        = useState((now.getMonth() + 1).toString().padStart(2, '0'));
  const [idProducto,  setIdProducto] = useState('');
  const [idDocente,   setIdDocente]  = useState('');
  const [filtroKpi,   setFiltroKpi]  = useState('');
  const [filtroRec,   setFiltroRec]  = useState('');

  // Estado modal "Ver más" — no necesita Redux, es UI local
  const [verMas, setVerMas] = useState({ tipo: null, datos: [], cargando: false });

  const anios = ['2026', '2025', '2024'];
  const meses = [
    { v: '01', l: 'Enero' },   { v: '02', l: 'Febrero' },   { v: '03', l: 'Marzo' },
    { v: '04', l: 'Abril' },   { v: '05', l: 'Mayo' },      { v: '06', l: 'Junio' },
    { v: '07', l: 'Julio' },   { v: '08', l: 'Agosto' },    { v: '09', l: 'Septiembre' },
    { v: '10', l: 'Octubre' }, { v: '11', l: 'Noviembre' }, { v: '12', l: 'Diciembre' },
  ];

  useEffect(() => {
    obtenerKpisAcademicos(anio, mes);
    obtenerProductosKpi(anio, mes);
    obtenerDocentesKpi(anio, mes);
  }, []);

  const consultar = () => {
    obtenerKpisAcademicos(anio, mes, idProducto || null, idDocente || null);
    obtenerProductosKpi(anio, mes);
    obtenerDocentesKpi(anio, mes, idProducto || null);
  };

  // Abrir modal de detalle (CA13 / CA16)
  const abrirDetalle = async (tipo) => {
    setVerMas({ tipo, datos: [], cargando: true });
    try {
      const endpoint = tipo === 'recursos'
        ? '/admin/kpis-academicos/detalle-recursos'
        : tipo === 'calidad'
        ? '/admin/kpis-academicos/detalle-calidad'
        : '/admin/kpis-academicos/detalle-implementacion';
      const params = { anio, mes };
      if (idProducto) params.id_proyecto = idProducto;
      if (idDocente)  params.id_docente  = idDocente;
      const res = await authAxios.get(endpoint, { params });
      setVerMas({ tipo, datos: res.data, cargando: false });
    } catch (e) {
      setVerMas({ tipo: null, datos: [], cargando: false });
      avisoError('Error al cargar el detalle');
    }
  };

  const sinDatos    = !datos || datos.sin_datos || datos.sesiones_programadas === 0;
  const recursos    = datos?.recursos || [];
  const calProds    = datos?.calidad_productos || [];

  const recursosFiltrados = filtroRec
    ? recursos.filter(r => r.nombre === filtroRec)
    : recursos;

  const estadoBloque = (pct) => {
    if (sinDatos || pct === null) return 'sin_datos';
    if (pct >= 90) return 'verde';
    if (pct >= 70) return 'amarillo';
    return 'rojo';
  };

  const pctImpl = sinDatos ? null : Math.round((datos.pct_confirmadas + datos.pct_confirmadas_7dias + datos.pct_capacitados) / 3);
  const pctRec  = sinDatos ? null : (datos.pct_recursos_3dias || 0);
  const pctCal  = sinDatos || !datos?.pct_calidad_docente ? null : datos.pct_calidad_docente;

  const bloqueVisible = (pct) => !filtroKpi || estadoBloque(pct) === filtroKpi;

  // CA17 — alertas automáticas
  const alertas = [];
  if (!sinDatos) {
    if (datos.pct_confirmadas < 90)
      alertas.push(`${datos.sesiones_programadas - datos.sesiones_confirmadas} sesión(es) sin confirmar`);
    if (datos.total_docentes > 0 && datos.pct_capacitados < 90)
      alertas.push(`${datos.total_docentes - datos.docentes_capacitados} docente(s) sin capacitar`);
    if (datos.pct_recursos_3dias < 85)
      alertas.push('Recursos incompletos o no cargados a tiempo');
    if (recursos.some(r => r.nombre === 'La clase Offline' && r.cumplidos < r.total))
      alertas.push('Sesiones con clase offline pendiente');
  }

  const periodo = `${meses.find(m => m.v === mes)?.l || mes} ${anio}`;

  // ── CA18: Exportar Excel (async con detalle) ─────────────────
  const exportarExcel = async () => {
    const estado = (pct, meta) => pct === null ? 'Sin datos' : pct >= meta ? 'Cumple' : pct >= meta * 0.78 ? 'En riesgo' : 'No cumple';
    const confirmadas_7dias = Math.round((datos.pct_confirmadas_7dias / 100) * datos.sesiones_programadas);
    const recursos_3dias    = Math.round((datos.pct_recursos_3dias    / 100) * datos.sesiones_programadas);

    const params = { anio, mes };
    if (idProducto) params.id_proyecto = idProducto;
    if (idDocente)  params.id_docente  = idDocente;

    let detImpl = [], detRec = [], detCal = [];
    try {
      const [r1, r2, r3] = await Promise.all([
        authAxios.get('/admin/kpis-academicos/detalle-implementacion', { params }),
        authAxios.get('/admin/kpis-academicos/detalle-recursos',       { params }),
        authAxios.get('/admin/kpis-academicos/detalle-calidad',        { params }),
      ]);
      detImpl = r1.data;
      detRec  = r2.data;
      detCal  = r3.data;
    } catch (e) { /* exportar igual con lo que hay */ }

    const wb = XLSX.utils.book_new();

    const ws1 = XLSX.utils.aoa_to_sheet([
      [`KPI's Académicos — ${periodo}`],
      [],
      ['KPI', 'Cumplidos', 'Total', '%', 'Meta', 'Estado'],
      ['Sesiones programadas',       datos.sesiones_programadas, '—', '—', '—', '—'],
      ['Sesiones confirmadas',       datos.sesiones_confirmadas, datos.sesiones_programadas, datos.pct_confirmadas + '%',        '90%', estado(datos.pct_confirmadas, 90)],
      ['Confirmación con 7 días',    confirmadas_7dias,          datos.sesiones_programadas, datos.pct_confirmadas_7dias + '%',  '80%', estado(datos.pct_confirmadas_7dias, 80)],
      ['Docentes capacitados',       datos.docentes_capacitados, datos.total_docentes,       datos.pct_capacitados + '%',        '90%', estado(datos.pct_capacitados, 90)],
      ['Capacitación con 5 días',    datos.docentes_cap_5dias || 0, datos.total_docentes,    (datos.pct_cap_5dias || 0) + '%',   '80%', estado(datos.pct_cap_5dias || 0, 80)],
      ['Recursos completos 3 días',  recursos_3dias,             datos.sesiones_programadas, datos.pct_recursos_3dias + '%',    '85%', estado(datos.pct_recursos_3dias, 85)],
      ['Calidad docente',            '—',                        '—',                         (datos.pct_calidad_docente || 0) + '%', '80%', estado(datos.pct_calidad_docente || 0, 80)],
    ]);
    XLSX.utils.book_append_sheet(wb, ws1, 'KPIs Resumen');

    const ws2 = XLSX.utils.aoa_to_sheet([
      ['Tipo de recurso', 'Cumplidos', 'Total', '%'],
      ...recursos.map(r => [r.nombre, r.cumplidos, r.total, r.total > 0 ? Math.round((r.cumplidos / r.total) * 100) + '%' : '—']),
    ]);
    XLSX.utils.book_append_sheet(wb, ws2, 'Recursos');

    const ws3 = XLSX.utils.aoa_to_sheet([
      ['Producto', 'Promedio', 'Encuestas', 'Meta', 'Estado'],
      ...calProds.map(c => [c.nombre, c.promedio + '%', c.total_encuestas, c.meta + '%',
        c.estado === 'verde' ? 'Cumple' : c.estado === 'amarillo' ? 'En riesgo' : 'No cumple']),
    ]);
    XLSX.utils.book_append_sheet(wb, ws3, 'Calidad Docente');

    if (detImpl.length > 0) {
      const ws4 = XLSX.utils.aoa_to_sheet([
        ['Producto', 'Sesión', 'Fecha', 'Confirmado', 'Fecha confirm.', 'Oportuna (≥7d)', 'Docente responsable', 'Observaciones'],
        ...detImpl.map(r => {
          const obs = !r.fecha_confirmacion ? 'Sin confirmación' : r.confirmacion_oportuna == 0 ? 'Confirmación tardía' : '—';
          return [r.nombre_proyecto, r.nombre_sesion, r.fecha_sesion,
            r.confirmado == 1 ? 'Sí' : 'No', r.fecha_confirmacion || '—',
            r.confirmacion_oportuna == 1 ? 'Oportuna' : r.fecha_confirmacion ? 'Tardía' : '—',
            r.docente_confirmado || 'Sin asignar', obs];
        }),
      ]);
      XLSX.utils.book_append_sheet(wb, ws4, 'Detalle Implementación');
    }

    if (detRec.length > 0) {
      const tiposRec = [...new Set(detRec.flatMap(s => Object.keys(s.recursos || {})))];
      const ws5 = XLSX.utils.aoa_to_sheet([
        ['Producto', 'Sesión', 'Fecha', 'Responsable', ...tiposRec, 'Estado', 'Observaciones'],
        ...detRec.map(r => {
          const obs = r.estado_general === 'Sin recursos' ? 'Sin recursos registrados'
            : r.estado_general === 'Incompleto' ? 'Recursos incompletos' : '—';
          return [r.nombre_proyecto, r.nombre_sesion, r.fecha_sesion, r.responsable || '—',
            ...tiposRec.map(t => r.recursos?.[t] || '—'), r.estado_general, obs];
        }),
      ]);
      XLSX.utils.book_append_sheet(wb, ws5, 'Detalle Recursos');
    }

    if (detCal.length > 0) {
      const ws6 = XLSX.utils.aoa_to_sheet([
        ['Producto', 'Sesión', 'Fecha', 'Docente', 'Calificación', 'Estado', 'Observaciones'],
        ...detCal.map(r => {
          const cal = parseFloat(r.calificacion) || 0;
          const est = cal >= 80 ? 'Cumple' : cal >= 64 ? 'En riesgo' : 'No cumple';
          const obs = cal < 64 ? 'Bajo desempeño' : cal < 80 ? 'Necesita mejorar' : '—';
          return [r.nombre_proyecto, r.nombre_sesion, r.fecha_sesion, r.docente || '—', cal + '%', est, obs];
        }),
      ]);
      XLSX.utils.book_append_sheet(wb, ws6, 'Detalle Calidad');
    }

    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buf], { type: 'application/octet-stream' }), `KPIs_Academicos_${periodo.replace(' ', '_')}.xlsx`);
  };

  // ── CA18: Exportar PDF (async con detalle) ───────────────────
  const exportarPdf = async () => {
    const estado = (pct, meta) => pct === null ? 'Sin datos' : pct >= meta ? 'Cumple' : pct >= meta * 0.78 ? 'En riesgo' : 'No cumple';
    const confirmadas_7dias = Math.round((datos.pct_confirmadas_7dias / 100) * datos.sesiones_programadas);
    const recursos_3dias    = Math.round((datos.pct_recursos_3dias    / 100) * datos.sesiones_programadas);

    const params = { anio, mes };
    if (idProducto) params.id_proyecto = idProducto;
    if (idDocente)  params.id_docente  = idDocente;

    let detImpl = [], detRec = [], detCal = [];
    try {
      const [r1, r2, r3] = await Promise.all([
        authAxios.get('/admin/kpis-academicos/detalle-implementacion', { params }),
        authAxios.get('/admin/kpis-academicos/detalle-recursos',       { params }),
        authAxios.get('/admin/kpis-academicos/detalle-calidad',        { params }),
      ]);
      detImpl = r1.data;
      detRec  = r2.data;
      detCal  = r3.data;
    } catch (e) { /* continuar con lo disponible */ }

    const doc = new jsPDF('p', 'mm', 'a4');

    doc.setFontSize(14);
    doc.setFontStyle('bold');
    doc.text("KPI's Académicos — INEDI Posgrados", 14, 18);
    doc.setFontSize(9);
    doc.setFontStyle('normal');
    doc.text(`Periodo: ${periodo}   |   Generado: ${new Date().toLocaleDateString('es-PE')}`, 14, 25);

    doc.autoTable({
      startY: 30,
      head: [['KPI', 'Cumplidos', 'Total', '%', 'Meta', 'Estado']],
      body: [
        ['Sesiones programadas',      datos.sesiones_programadas, '—', '—', '—', '—'],
        ['Sesiones confirmadas',       datos.sesiones_confirmadas, datos.sesiones_programadas, datos.pct_confirmadas + '%',        '90%', estado(datos.pct_confirmadas, 90)],
        ['Confirmación con 7 días',    confirmadas_7dias,          datos.sesiones_programadas, datos.pct_confirmadas_7dias + '%',  '80%', estado(datos.pct_confirmadas_7dias, 80)],
        ['Docentes capacitados',       datos.docentes_capacitados, datos.total_docentes,       datos.pct_capacitados + '%',        '90%', estado(datos.pct_capacitados, 90)],
        ['Capacitación con 5 días',    datos.docentes_cap_5dias || 0, datos.total_docentes,    (datos.pct_cap_5dias || 0) + '%',  '80%', estado(datos.pct_cap_5dias || 0, 80)],
        ['Recursos completos 3 días',  recursos_3dias,             datos.sesiones_programadas, datos.pct_recursos_3dias + '%',    '85%', estado(datos.pct_recursos_3dias, 85)],
        ['Calidad docente',            '—',                        '—', (datos.pct_calidad_docente || 0) + '%', '80%', estado(datos.pct_calidad_docente || 0, 80)],
      ],
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [52, 58, 64], textColor: 255, fontStyle: 'bold' },
      theme: 'striped',
    });

    if (recursos.length > 0) {
      const y1 = doc.lastAutoTable.finalY + 8;
      doc.setFontSize(11);
      doc.setFontStyle('bold');
      doc.text('Recursos por Tipo', 14, y1);
      doc.autoTable({
        startY: y1 + 4,
        head: [['Tipo de recurso', 'Cumplidos', 'Total', '%']],
        body: recursos.map(r => [r.nombre, r.cumplidos, r.total, r.total > 0 ? Math.round((r.cumplidos / r.total) * 100) + '%' : '—']),
        styles: { fontSize: 9, cellPadding: 3 },
        headStyles: { fillColor: [40, 167, 69], textColor: 255 },
        theme: 'striped',
      });
    }

    if (calProds.length > 0) {
      const y2 = doc.lastAutoTable.finalY + 8;
      doc.setFontSize(11);
      doc.setFontStyle('bold');
      doc.text('Calidad Docente por Producto', 14, y2);
      doc.autoTable({
        startY: y2 + 4,
        head: [['Producto', 'Promedio', 'Encuestas', 'Meta', 'Estado']],
        body: calProds.map(c => [c.nombre, c.promedio + '%', c.total_encuestas, c.meta + '%',
          c.estado === 'verde' ? 'Cumple' : c.estado === 'amarillo' ? 'En riesgo' : 'No cumple']),
        styles: { fontSize: 9, cellPadding: 3 },
        headStyles: { fillColor: [253, 126, 20], textColor: 255 },
        theme: 'striped',
      });
    }

    if (detImpl.length > 0) {
      doc.addPage();
      doc.setFontSize(12);
      doc.setFontStyle('bold');
      doc.text('Detalle — Implementación Académica', 14, 16);
      doc.autoTable({
        startY: 20,
        head: [['Producto', 'Sesión', 'Fecha', 'Confirmado', 'Fecha conf.', 'Oportuna', 'Docente', 'Observaciones']],
        body: detImpl.map(r => {
          const obs = !r.fecha_confirmacion ? 'Sin confirmación' : r.confirmacion_oportuna == 0 ? 'Conf. tardía' : '—';
          return [r.nombre_proyecto, r.nombre_sesion, r.fecha_sesion,
            r.confirmado == 1 ? 'Sí' : 'No', r.fecha_confirmacion || '—',
            r.confirmacion_oportuna == 1 ? 'Oportuna' : r.fecha_confirmacion ? 'Tardía' : '—',
            r.docente_confirmado || '—', obs];
        }),
        styles: { fontSize: 7, cellPadding: 2 },
        headStyles: { fillColor: [52, 58, 64], textColor: 255, fontStyle: 'bold', fontSize: 7 },
        theme: 'striped',
      });
    }

    if (detRec.length > 0) {
      doc.addPage();
      doc.setFontSize(12);
      doc.setFontStyle('bold');
      doc.text('Detalle — Organización de Recursos', 14, 16);
      const tiposRec = [...new Set(detRec.flatMap(s => Object.keys(s.recursos || {})))];
      doc.autoTable({
        startY: 20,
        head: [['Producto', 'Sesión', 'Fecha', 'Responsable', ...tiposRec.map(t => t.replace('La clase ', '').replace('Los ', '').replace('La ', '')), 'Estado', 'Observaciones']],
        body: detRec.map(r => {
          const obs = r.estado_general === 'Sin recursos' ? 'Sin recursos' : r.estado_general === 'Incompleto' ? 'Incompleto' : '—';
          return [r.nombre_proyecto, r.nombre_sesion, r.fecha_sesion, r.responsable || '—',
            ...tiposRec.map(t => r.recursos?.[t] || '—'), r.estado_general, obs];
        }),
        styles: { fontSize: 7, cellPadding: 2 },
        headStyles: { fillColor: [40, 167, 69], textColor: 255, fontStyle: 'bold', fontSize: 7 },
        theme: 'striped',
      });
    }

    if (detCal.length > 0) {
      doc.addPage();
      doc.setFontSize(12);
      doc.setFontStyle('bold');
      doc.text('Detalle — Calidad Docente', 14, 16);
      doc.autoTable({
        startY: 20,
        head: [['Producto', 'Sesión', 'Fecha', 'Docente', 'Calificación', 'Estado', 'Observaciones']],
        body: detCal.map(r => {
          const cal = parseFloat(r.calificacion) || 0;
          const est = cal >= 80 ? 'Cumple' : cal >= 64 ? 'En riesgo' : 'No cumple';
          const obs = cal < 64 ? 'Bajo desempeño' : cal < 80 ? 'Necesita mejorar' : '—';
          return [r.nombre_proyecto, r.nombre_sesion, r.fecha_sesion, r.docente || '—', cal + '%', est, obs];
        }),
        styles: { fontSize: 7, cellPadding: 2 },
        headStyles: { fillColor: [253, 126, 20], textColor: 255, fontStyle: 'bold', fontSize: 7 },
        theme: 'striped',
      });
    }

    doc.save(`KPIs_Academicos_${periodo.replace(' ', '_')}.pdf`);
  };

  // Columnas dinámicas para modal recursos (CA13)
  const tiposRecurso = verMas.datos.length > 0 && verMas.tipo === 'recursos'
    ? [...new Set(verMas.datos.flatMap(s => Object.keys(s.recursos || {})))]
    : [];

  return (
    <Layout>
      <HeaderContent title="Reporte KPI's Académicos" />
      <Breadcrumb
        link={[
          { url: '/inicio', nombre: 'Inicio', icon: 'fas fa-home' },
          { url: '',        nombre: "Reporte KPI's Académicos" },
        ]}
      />

      {/* ── Filtros ──────────────────────────────────────── */}
      <div className="box p-4 mb-3">
        <div className="row align-items-end">
          <div className="col-md-2 mb-2">
            <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 5 }}>
              <FontAwesomeIcon icon={faCalendarAlt} className="mr-1 text-muted" />Año
            </label>
            <select className="form-control" value={anio} onChange={e => setAnio(e.target.value)}>
              {anios.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div className="col-md-2 mb-2">
            <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 5 }}>
              <FontAwesomeIcon icon={faCalendarAlt} className="mr-1 text-muted" />Mes
            </label>
            <select className="form-control" value={mes} onChange={e => setMes(e.target.value)}>
              {meses.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
            </select>
          </div>
          <div className="col-md-2 mb-2">
            <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 5 }}>Producto</label>
            <select className="form-control" value={idProducto} onChange={e => setIdProducto(e.target.value)}>
              <option value="">Todos los productos</option>
              {productos.map(p => (
                <option key={p.id_proyecto} value={p.id_proyecto}>{p.nombre_proyecto}</option>
              ))}
            </select>
          </div>
          <div className="col-md-2 mb-2">
            <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 5 }}>Docente</label>
            <select className="form-control" value={idDocente} onChange={e => setIdDocente(e.target.value)}>
              <option value="">Todos los docentes</option>
              {docentes.map(d => (
                <option key={d.id_docente} value={d.id_docente}>{d.nombre_docente}</option>
              ))}
            </select>
          </div>
          <div className="col-md-2 mb-2">
            <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 5 }}>Estado KPI</label>
            <select className="form-control" value={filtroKpi} onChange={e => setFiltroKpi(e.target.value)}>
              <option value="">Todos</option>
              <option value="verde">Cumple</option>
              <option value="amarillo">En riesgo</option>
              <option value="rojo">No cumple</option>
              <option value="sin_datos">Sin datos</option>
            </select>
          </div>
          <div className="col-md-1 mb-2">
            <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 5 }}>Recurso</label>
            <select className="form-control" value={filtroRec} onChange={e => setFiltroRec(e.target.value)}>
              <option value="">Todos</option>
              {recursos.map((r, i) => <option key={i} value={r.nombre}>{r.nombre}</option>)}
            </select>
          </div>
          <div className="col-md-1 mb-2" style={{ paddingTop: 22 }}>
            <Button variant="primary" block onClick={consultar} disabled={loading}>
              <FontAwesomeIcon icon={faFilter} />
            </Button>
          </div>
        </div>
      </div>

      {/* ── CA18: Botones de exportación ─────────────────── */}
      {!sinDatos && (
        <div className="d-flex justify-content-end mb-3">
          <Button variant="outline-success" size="sm" className="mr-2" onClick={exportarExcel} disabled={loading}>
            <FontAwesomeIcon icon={faFileExcel} className="mr-1" />Excel
          </Button>
          <Button variant="outline-danger" size="sm" onClick={exportarPdf} disabled={loading}>
            <FontAwesomeIcon icon={faFilePdf} className="mr-1" />PDF
          </Button>
        </div>
      )}

      <SpinnerLoading loading={loading}>

        {/* ── Alertas CA17 ─────────────────────────────────── */}
        {alertas.length > 0 && (
          <div className="mb-3">
            {alertas.map((a, i) => (
              <div key={i} className="d-flex align-items-center p-2 mb-1"
                style={{ backgroundColor: '#fff3cd', borderRadius: 6, fontSize: 13 }}>
                <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2 text-warning" />
                {a}
              </div>
            ))}
          </div>
        )}

        {/* ── Tarjetas superiores CA3 ──────────────────────── */}
        <div className="row">
          <div className="col-xl-3 col-md-4 col-sm-6 mb-3">
            <div className="box p-3 h-100" style={{ borderTop: '4px solid #17a2b8' }}>
              <div className="d-flex align-items-start mb-2">
                <div style={{ width: 38, height: 38, borderRadius: 8, backgroundColor: '#17a2b820', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <FontAwesomeIcon icon={faCalendarAlt} style={{ color: '#17a2b8', fontSize: 17 }} />
                </div>
              </div>
              <h2 style={{ color: '#17a2b8', fontWeight: 700, fontSize: 30, marginBottom: 2 }}>
                {sinDatos ? '—' : datos.sesiones_programadas}
              </h2>
              <p className="mb-0" style={{ fontSize: 12, fontWeight: 600, color: '#444' }}>Sesiones programadas</p>
            </div>
          </div>
          <KpiCard titulo="Sesiones confirmadas"      valor={sinDatos ? 0 : datos.pct_confirmadas}      meta={90} icon={faCheckCircle}   sinDatos={sinDatos} />
          <KpiCard titulo="Confirmación con 7 días"   valor={sinDatos ? 0 : datos.pct_confirmadas_7dias} meta={80} icon={faClock}         sinDatos={sinDatos} />
          <KpiCard titulo="Docentes capacitados"      valor={sinDatos ? 0 : datos.pct_capacitados}       meta={90} icon={faUserGraduate}  sinDatos={sinDatos} />
          <KpiCard titulo="Capacitación con 5 días"   valor={sinDatos ? 0 : datos.pct_cap_5dias}  meta={80} icon={faUserCheck} sinDatos={sinDatos} />
          <KpiCard titulo="Recursos completos 3 días" valor={sinDatos ? 0 : datos.pct_recursos_3dias}    meta={85} icon={faClipboardCheck} sinDatos={sinDatos} />
          <KpiCard titulo="Calidad docente"           valor={sinDatos ? 0 : (datos.pct_calidad_docente || 0)} meta={80} icon={faStar} sinDatos={sinDatos || !datos?.pct_calidad_docente} />
        </div>

        {/* ── Bloque 1: Implementación Académica ───────────── */}
        {bloqueVisible(pctImpl) && (
          <BloqueKpi
            titulo="Implementación Académica"
            icon={faChartLine}
            pct={pctImpl}
            sinDatos={sinDatos}
            onVerMas={!sinDatos ? () => abrirDetalle('implementacion') : null}
          >
            <KpiFila label="Sesiones confirmadas"                    cumplidos={datos?.sesiones_confirmadas || 0} total={datos?.sesiones_programadas || 0} />
            <KpiFila label="Confirmación con 7 días de anticipación" cumplidos={sinDatos ? 0 : Math.round((datos.pct_confirmadas_7dias / 100) * datos.sesiones_programadas)} total={datos?.sesiones_programadas || 0} />
            <KpiFila label="Docentes capacitados"                    cumplidos={datos?.docentes_capacitados || 0} total={datos?.total_docentes || 0} />
            <KpiFila label="Capacitación con 5 días de anticipación" cumplidos={datos?.docentes_cap_5dias || 0} total={datos?.total_docentes || 0} />
          </BloqueKpi>
        )}

        {/* ── Bloque 2: Organización de Recursos ───────────── */}
        {bloqueVisible(pctRec) && (
          <BloqueKpi
            titulo="Organización de Recursos"
            icon={faBook}
            pct={pctRec}
            sinDatos={sinDatos}
            onVerMas={!sinDatos ? () => abrirDetalle('recursos') : null}
          >
            {recursosFiltrados.length === 0 ? (
              <p className="text-muted text-center py-3" style={{ fontSize: 13 }}>Sin registros de recursos para el periodo seleccionado.</p>
            ) : (
              recursosFiltrados.map((r, i) => (
                <KpiFila key={i} label={r.nombre} cumplidos={r.cumplidos} total={r.total} obligatorio={r.nombre === 'La clase Offline'} />
              ))
            )}
            {recursosFiltrados.length > 0 && (
              <div className="mt-3 pt-2" style={{ borderTop: '2px solid #e9ecef' }}>
                <KpiFila
                  label="Recursos completos 3 días antes de la sesión"
                  cumplidos={sinDatos ? 0 : Math.round((datos.pct_recursos_3dias / 100) * datos.sesiones_programadas)}
                  total={datos?.sesiones_programadas || 0}
                />
              </div>
            )}
          </BloqueKpi>
        )}

        {/* ── Bloque 3: Control de Calidad Docente CA14 ────── */}
        {bloqueVisible(pctCal) && (
          <BloqueKpi titulo="Control de Calidad Docente" icon={faStar} pct={pctCal} sinDatos={sinDatos || !datos?.pct_calidad_docente}
            onVerMas={!sinDatos && datos?.pct_calidad_docente ? () => abrirDetalle('calidad') : null}>
            {calProds.length === 0 ? (
              <p className="text-muted text-center py-3" style={{ fontSize: 13 }}>Sin registros de calidad docente para el periodo seleccionado.</p>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm table-bordered mb-0" style={{ fontSize: 13 }}>
                  <thead className="thead-light">
                    <tr>
                      <th>Producto</th>
                      <th className="text-center" style={{ width: 100 }}>Promedio</th>
                      <th className="text-center" style={{ width: 110 }}>Encuestas</th>
                      <th className="text-center" style={{ width: 80 }}>Meta</th>
                      <th className="text-center" style={{ width: 110 }}>Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {calProds.map((c, i) => {
                      const color = colorEstado[c.estado] || '#6c757d';
                      return (
                        <tr key={i}>
                          <td className="align-middle">{c.nombre}</td>
                          <td className="text-center align-middle">
                            <span style={{ fontWeight: 700, color }}>{c.promedio}%</span>
                          </td>
                          <td className="text-center align-middle">{c.total_encuestas}</td>
                          <td className="text-center align-middle text-muted">{c.meta}%</td>
                          <td className="text-center align-middle">
                            <span style={{ fontSize: 11, fontWeight: 600, color, backgroundColor: `${color}15`, padding: '2px 8px', borderRadius: 20 }}>
                              {c.estado === 'verde' ? 'Cumple' : c.estado === 'amarillo' ? 'En riesgo' : 'No cumple'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </BloqueKpi>
        )}

      </SpinnerLoading>

      {/* ── Modal detalle — Implementación Académica (CA16) ── */}
      <Modal show={verMas.tipo === 'implementacion'} onHide={() => setVerMas({ tipo: null, datos: [], cargando: false })} size="xl">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 15 }}>
            <FontAwesomeIcon icon={faChartLine} className="mr-2 text-primary" />
            Detalle — Implementación Académica
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '12px 16px' }}>
          {verMas.cargando ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" size="sm" className="mr-2" />
              <span style={{ fontSize: 13 }}>Cargando detalle...</span>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm table-bordered table-hover mb-0" style={{ fontSize: 12 }}>
                <thead className="thead-light">
                  <tr>
                    <th className="text-center" style={{ width: 35 }}>#</th>
                    <th>Producto</th>
                    <th>Sesión</th>
                    <th className="text-center" style={{ width: 95 }}>Fecha</th>
                    <th className="text-center" style={{ width: 100 }}>Confirmado</th>
                    <th className="text-center" style={{ width: 110 }}>Fecha confirm.</th>
                    <th className="text-center" style={{ width: 110 }}>Oportuna (≥7d)</th>
                    <th>Docente responsable</th>
                    <th>Observaciones</th>
                  </tr>
                </thead>
                <tbody>
                  {verMas.datos.length === 0 ? (
                    <tr><td colSpan="9" className="text-center text-muted py-3">Sin registros</td></tr>
                  ) : verMas.datos.map((row, i) => {
                    const obs = !row.fecha_confirmacion ? 'Sin confirmación'
                      : row.confirmacion_oportuna == 0 ? 'Confirmación tardía' : '—';
                    const obsColor = !row.fecha_confirmacion ? '#dc3545'
                      : row.confirmacion_oportuna == 0 ? '#fd7e14' : '#6c757d';
                    return (
                      <tr key={i}>
                        <td className="text-center align-middle">{i + 1}</td>
                        <td className="align-middle" style={{ maxWidth: 180, wordBreak: 'break-word' }}>{row.nombre_proyecto}</td>
                        <td className="align-middle">{row.nombre_sesion}</td>
                        <td className="text-center align-middle">{row.fecha_sesion}</td>
                        <td className="text-center align-middle">
                          {row.confirmado == 1
                            ? <span style={{ color: '#28a745', fontWeight: 600 }}>✓ Sí</span>
                            : <span style={{ color: '#dc3545', fontWeight: 600 }}>✗ No</span>
                          }
                        </td>
                        <td className="text-center align-middle text-muted">{row.fecha_confirmacion || '—'}</td>
                        <td className="text-center align-middle">
                          {row.fecha_confirmacion
                            ? (row.confirmacion_oportuna == 1
                              ? <span style={{ color: '#28a745', fontWeight: 600, fontSize: 11 }}>Oportuna</span>
                              : <span style={{ color: '#fd7e14', fontWeight: 600, fontSize: 11 }}>Tardía</span>)
                            : <span className="text-muted" style={{ fontSize: 11 }}>—</span>
                          }
                        </td>
                        <td className="align-middle" style={{ fontSize: 12 }}>
                          {row.docente_confirmado || <span className="text-muted">Sin asignar</span>}
                        </td>
                        <td className="align-middle">
                          <span style={{ fontSize: 10, fontWeight: 600, color: obsColor }}>{obs}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer style={{ padding: '8px 16px' }}>
          <small className="text-muted mr-auto">{verMas.datos.length} sesión(es)</small>
          <Button variant="secondary" size="sm" onClick={() => setVerMas({ tipo: null, datos: [], cargando: false })}>Cerrar</Button>
        </Modal.Footer>
      </Modal>

      {/* ── Modal detalle — Organización de Recursos (CA13 / CA16) ── */}
      <Modal show={verMas.tipo === 'recursos'} onHide={() => setVerMas({ tipo: null, datos: [], cargando: false })} size="xl">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 15 }}>
            <FontAwesomeIcon icon={faBook} className="mr-2 text-success" />
            Detalle — Organización de Recursos
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '12px 16px' }}>
          {verMas.cargando ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="success" size="sm" className="mr-2" />
              <span style={{ fontSize: 13 }}>Cargando detalle...</span>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm table-bordered table-hover mb-0" style={{ fontSize: 12 }}>
                <thead className="thead-light">
                  <tr>
                    <th className="text-center" style={{ width: 35 }}>#</th>
                    <th>Producto</th>
                    <th>Sesión</th>
                    <th className="text-center" style={{ width: 90 }}>Fecha</th>
                    <th>Responsable</th>
                    {tiposRecurso.map(t => (
                      <th key={t} className="text-center" style={{ minWidth: 90, fontSize: 11 }}>
                        {t.replace('La clase ', '').replace('Los ', '').replace('La ', '')}
                        {t === 'La clase Offline' && <Badge variant="warning" className="ml-1" style={{ fontSize: 9 }}>Oblig.</Badge>}
                      </th>
                    ))}
                    <th className="text-center" style={{ width: 100 }}>Estado</th>
                    <th style={{ minWidth: 120 }}>Observaciones</th>
                  </tr>
                </thead>
                <tbody>
                  {verMas.datos.length === 0 ? (
                    <tr><td colSpan={6 + tiposRecurso.length} className="text-center text-muted py-3">Sin registros</td></tr>
                  ) : verMas.datos.map((row, i) => {
                    const color = row.estado_general === 'Completo' ? '#28a745'
                      : row.estado_general === 'Incompleto' ? '#dc3545' : '#6c757d';
                    const obs = row.estado_general === 'Sin recursos' ? 'Sin recursos registrados'
                      : row.estado_general === 'Incompleto' ? 'Recursos incompletos' : '—';
                    return (
                      <tr key={i}>
                        <td className="text-center align-middle">{i + 1}</td>
                        <td className="align-middle" style={{ maxWidth: 180, wordBreak: 'break-word' }}>{row.nombre_proyecto}</td>
                        <td className="align-middle">{row.nombre_sesion}</td>
                        <td className="text-center align-middle">{row.fecha_sesion}</td>
                        <td className="align-middle" style={{ fontSize: 11 }}>{row.responsable || <span className="text-muted">—</span>}</td>
                        {tiposRecurso.map(t => (
                          <td key={t} className="text-center align-middle">
                            <BadgeRecurso estado={row.recursos?.[t]} />
                          </td>
                        ))}
                        <td className="text-center align-middle">
                          <span style={{ fontSize: 10, fontWeight: 600, color, backgroundColor: `${color}20`, padding: '2px 6px', borderRadius: 10 }}>
                            {row.estado_general}
                          </span>
                        </td>
                        <td className="align-middle">
                          <span style={{ fontSize: 10, fontWeight: 600, color }}>{obs}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer style={{ padding: '8px 16px' }}>
          <small className="text-muted mr-auto">{verMas.datos.length} sesión(es)</small>
          <Button variant="secondary" size="sm" onClick={() => setVerMas({ tipo: null, datos: [], cargando: false })}>Cerrar</Button>
        </Modal.Footer>
      </Modal>

      {/* ── Modal detalle — Calidad Docente (CA16) ── */}
      <Modal show={verMas.tipo === 'calidad'} onHide={() => setVerMas({ tipo: null, datos: [], cargando: false })} size="xl">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 15 }}>
            <FontAwesomeIcon icon={faStar} className="mr-2 text-warning" />
            Detalle — Calidad Docente
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '12px 16px' }}>
          {verMas.cargando ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="warning" size="sm" className="mr-2" />
              <span style={{ fontSize: 13 }}>Cargando detalle...</span>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm table-bordered table-hover mb-0" style={{ fontSize: 12 }}>
                <thead className="thead-light">
                  <tr>
                    <th className="text-center" style={{ width: 35 }}>#</th>
                    <th>Producto</th>
                    <th>Sesión</th>
                    <th className="text-center" style={{ width: 95 }}>Fecha</th>
                    <th>Docente</th>
                    <th className="text-center" style={{ width: 100 }}>Calificación</th>
                    <th className="text-center" style={{ width: 110 }}>Estado</th>
                    <th style={{ minWidth: 130 }}>Observaciones</th>
                  </tr>
                </thead>
                <tbody>
                  {verMas.datos.length === 0 ? (
                    <tr><td colSpan="8" className="text-center text-muted py-3">Sin registros</td></tr>
                  ) : verMas.datos.map((row, i) => {
                    const cal   = parseFloat(row.calificacion) || 0;
                    const color = cal >= 80 ? '#28a745' : cal >= 64 ? '#fd7e14' : '#dc3545';
                    const label = cal >= 80 ? 'Cumple' : cal >= 64 ? 'En riesgo' : 'No cumple';
                    const obs   = cal < 64 ? 'Bajo desempeño' : cal < 80 ? 'Necesita mejorar' : '—';
                    return (
                      <tr key={i}>
                        <td className="text-center align-middle">{i + 1}</td>
                        <td className="align-middle" style={{ maxWidth: 180, wordBreak: 'break-word' }}>{row.nombre_proyecto}</td>
                        <td className="align-middle">{row.nombre_sesion}</td>
                        <td className="text-center align-middle">{row.fecha_sesion}</td>
                        <td className="align-middle">{row.docente || <span className="text-muted">—</span>}</td>
                        <td className="text-center align-middle">
                          <span style={{ fontWeight: 700, color }}>{cal}%</span>
                        </td>
                        <td className="text-center align-middle">
                          <span style={{ fontSize: 11, fontWeight: 600, color, backgroundColor: `${color}15`, padding: '2px 8px', borderRadius: 20 }}>
                            {label}
                          </span>
                        </td>
                        <td className="align-middle">
                          <span style={{ fontSize: 10, fontWeight: 600, color }}>{obs}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer style={{ padding: '8px 16px' }}>
          <small className="text-muted mr-auto">{verMas.datos.length} sesión(es)</small>
          <Button variant="secondary" size="sm" onClick={() => setVerMas({ tipo: null, datos: [], cargando: false })}>Cerrar</Button>
        </Modal.Footer>
      </Modal>

    </Layout>
  );
};

const mapStateToProps = state => ({
  loading:   state.kpisAcademicos.loading,
  datos:     state.kpisAcademicos.datos,
  productos: state.kpisAcademicos.productos,
  docentes:  state.kpisAcademicos.docentes,
});

const mapDispatchToProps = { obtenerKpisAcademicos, obtenerProductosKpi, obtenerDocentesKpi };

export default connect(mapStateToProps, mapDispatchToProps)(ReporteKpis);
